import { useMemo, useState } from "react";
import type { EntityProgressRow } from "@shared/types/runs";
import { connectionsReportingRows } from "./runFormatting";

const NONE: ReadonlySet<string> = new Set();

/**
 * #2389: the connections whose connector reports rows as it reads, held for the rest of the run.
 * `connectionsReportingRows` reads only what the rows show now, and the tables that were ticking
 * complete one after another: without the hold, the moment the last of them completes every
 * queued table of the connection would flip from "Waiting to read" back to "Extracting" although
 * nothing changed for it. `scope` is the run: a new run starts with nothing held.
 */
export function useReportingConnections(
  rows: readonly EntityProgressRow[],
  scope: string | undefined,
): ReadonlySet<string> {
  const current = useMemo(() => connectionsReportingRows(rows), [rows]);
  const [held, setHeld] = useState<{ scope: string | undefined; ids: ReadonlySet<string> }>(() => ({
    scope,
    ids: current,
  }));
  const heldIds = held.scope === scope ? held.ids : NONE;
  if (held.scope === scope && [...current].every((id) => heldIds.has(id))) return heldIds;
  // Adjusting state while rendering (React's pattern for state derived from a changing input):
  // React re-renders at once with the new set, and the check above then holds.
  const ids = new Set([...heldIds, ...current]);
  setHeld({ scope, ids });
  return ids;
}
